import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";

// Assumptions — tune against real network conditions / API error shape:
const REDIRECT_DELAY_MS = 400; // small pause so the UI has a moment to paint
const FALLBACK_DELAY_MS = 2500; // how long to wait before showing the manual link

const ShortUrlRedirect = () => {
  const { shortCode } = useParams();
  const [status, setStatus] = useState("loading"); // loading | redirecting | error
  const [originalUrl, setOriginalUrl] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showFallbackLink, setShowFallbackLink] = useState(false);

  useEffect(() => {
    let redirectTimer;
    let fallbackTimer;

    const redirectToOriginalUrl = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_URL}/short-url/${shortCode}`,
        );
        const url = response?.data?.data?.originalUrl;

        if (!url) {
          throw new Error("Original URL not found");
        }

        setOriginalUrl(url);
        setStatus("redirecting");

        redirectTimer = window.setTimeout(() => {
          window.location.replace(url);
        }, REDIRECT_DELAY_MS);

        // If we're still on this page after a few seconds, the auto-redirect
        // likely stalled (slow network, popup/nav block) — offer a manual way out.
        fallbackTimer = window.setTimeout(() => {
          setShowFallbackLink(true);
        }, FALLBACK_DELAY_MS);
      } catch (error) {
        console.error("Short URL redirect failed:", error);
        setStatus("error");
        setErrorMessage(
          error?.response?.status === 404
            ? "This link doesn't exist or may have expired."
            : "Something went wrong while redirecting. Please try again.",
        );
      }
    };

    if (shortCode) {
      redirectToOriginalUrl();
    } else {
      setStatus("error");
      setErrorMessage("No link was provided.");
    }

    return () => {
      window.clearTimeout(redirectTimer);
      window.clearTimeout(fallbackTimer);
    };
  }, [shortCode]);

  return (
    <div
      className="flex items-center justify-center h-screen"
      style={{ background: "#fff" }}
    >
      <style>{`
        @keyframes shortUrlBounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        @keyframes shortUrlPulse {
          0%, 100% { opacity: .3; }
          50% { opacity: 1; }
        }
      `}</style>

      {status === "error" ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 12,
            textAlign: "center",
            padding: "0 20px",
          }}
        >
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 16,
              background: "#FBEAE9",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <LinkOffIcon style={{ fontSize: 24, color: "#B23A34" }} />
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#1C2029" }}>
            {errorMessage || "We couldn't open this link."}
          </div>
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 16,
              background: "#FBEEE2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              animation: "shortUrlBounce 1s ease-in-out infinite",
            }}
          >
            <LinkIcon style={{ fontSize: 24, color: "#B8631F" }} />
          </div>

          <div style={{ fontSize: 14, fontWeight: 600, color: "#1C2029" }}>
            Hang tight, opening your link
          </div>

          <div style={{ display: "flex", gap: 5 }}>
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: "#B8631F",
                animation: "shortUrlPulse 1.2s ease-in-out infinite",
              }}
            />
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: "#B8631F",
                animation: "shortUrlPulse 1.2s ease-in-out .2s infinite",
              }}
            />
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: "#B8631F",
                animation: "shortUrlPulse 1.2s ease-in-out .4s infinite",
              }}
            />
          </div>

          {showFallbackLink && originalUrl && (
            <a
              href={originalUrl}
              style={{
                marginTop: 6,
                fontSize: 12.5,
                color: "#2C6FE8",
                fontWeight: 600,
                textDecoration: "underline",
              }}
            >
              Taking a while — tap here to continue →
            </a>
          )}
        </div>
      )}
    </div>
  );
};

export default ShortUrlRedirect;
